import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Plus, Check, MapPin, Building2, Download, Eye, Pencil, X } from "lucide-react";
import { istToday } from "@/lib/dateFormat";

const STATUS_COLORS = { draft: "bg-amber-100 text-amber-800", active: "bg-emerald-100 text-emerald-800" };
const TYPE_ICONS = { plant: "P", warehouse: "W", zone: "Z", bin: "B", "3p_virtual_warehouse": "V" };

function isAdmin(user) {
  return (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
}

const EMPTY_LOC = {
  location_name: "", location_type: "warehouse", parent_location_id: null,
  warehouse_ref_id: "", address: { line1: "", city: "", state: "", pincode: "" },
  capacity_units: 0, capacity_uom: "", storage_condition: "ambient",
  is_virtual: false, virtual_vendor_id: "",
  contact_person_name: "", contact_phone: "", contact_email: "", nearest_landmark: "",
};

export default function OpsLocationsPage() {
  const { getValues } = useConfig();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const locationTypes = getValues("ops_location_types");
  const storageConditions = getValues("ops_storage_conditions");
  const uomList = getValues("ops_uom_list");

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewLoc, setViewLoc] = useState(null);
  const [editId, setEditId] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterLocStatus, setFilterLocStatus] = useState("");
  const [filterStorage, setFilterStorage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_LOC });
  const [saving, setSaving] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.location_type = filterType;
      if (filterSearch) params.search = filterSearch;
      if (filterLocStatus) params.status = filterLocStatus;
      if (filterStorage) params.storage_condition = filterStorage;
      const { data } = await api.get("/ops/inventory/locations", { params });
      setLocations(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [filterType, filterSearch, filterLocStatus, filterStorage]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  useEffect(() => {
    api.get("/warehouses").then(({ data }) => setWarehouses(Array.isArray(data) ? data : [])).catch(() => {});
    if (admin) api.get("/ops/pending-counts").then(({ data }) => setPendingCount(data.location_master || 0)).catch(() => {});
  }, [admin]);

  const parentCandidates = locations.filter(l => l.status === "active" && ["plant", "warehouse", "zone"].includes(l.location_type));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) {
        const { data } = await api.put(`/ops/inventory/locations/${editId}`, formData);
        toast.success(data?.has_pending_changes ? "Edit submitted for approval" : "Location updated");
      } else {
        await api.post("/ops/inventory/locations", formData);
        toast.success("Location created as draft");
      }
      setShowForm(false);
      setFormData({ ...EMPTY_LOC });
      fetchLocations();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/ops/inventory/locations/${id}/approve`);
      toast.success("Location approved & active");
      fetchLocations();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const getParentName = (pid) => {
    if (!pid) return "—";
    const p = locations.find(l => l._id === pid);
    return p ? `${p.location_code} — ${p.location_name}` : pid;
  };

  return (
    <div className="space-y-4" data-testid="ops-locations-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="ops-locations-title">Location Master</h1>
        <div className="flex gap-2">
          {admin && <Button variant="outline" onClick={() => { const h = ["Location Code","Location Name","Location Type","Storage Condition","Status","Contact Person","Contact Phone","Landmark","Created Date"]; const rows = locations.map(l => [l.location_code||"",l.location_name||"",l.location_type||"",l.storage_condition||"",l.status||"",l.contact_person_name||"",l.contact_phone||"",l.nearest_landmark||"",(l.created_at||"").slice(0,10)]); const csv = [h,...rows].map(r => r.map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(",")).join("\n"); const bl = new Blob([csv],{type:"text/csv"}); const a = document.createElement("a"); a.href = URL.createObjectURL(bl); a.download = `location_master_${istToday()}.csv`; a.click(); }} data-testid="export-locations-csv"><Download className="w-4 h-4 mr-1" />Export CSV</Button>}
          <Button onClick={() => { setEditId(null); setFormData({ ...EMPTY_LOC }); setShowForm(true); }} data-testid="create-location-btn"><Plus className="w-4 h-4 mr-1" />New Location</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 items-end">
        <Input placeholder="Search code or name..." value={filterSearch} onChange={e => setFilterSearch(e.target.value)} className="w-48" />
        <Select value={filterLocStatus} onValueChange={v => setFilterLocStatus(v === "all" ? "" : v)}><SelectTrigger className="w-32"><SelectValue placeholder="All Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent></Select>
        <Select value={filterStorage} onValueChange={v => setFilterStorage(v === "all" ? "" : v)}><SelectTrigger className="w-36"><SelectValue placeholder="All Storage" /></SelectTrigger><SelectContent><SelectItem value="all">All Storage</SelectItem>{storageConditions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
      </div>

      {admin && pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center justify-between" data-testid="locations-pending-banner">
          <span className="text-sm text-amber-800 font-medium">{pendingCount} location(s) awaiting your approval</span>
        </div>
      )}

      <div className="flex gap-3 items-end" data-testid="locations-filter-bar">
        <Select value={filterType} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="locations-filter-type"><SelectValue placeholder="All Location Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Location Types</SelectItem>
            {locationTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg overflow-auto" data-testid="locations-table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="w-12"></TableHead>
              <TableHead className="w-28">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-40">Type</TableHead>
              <TableHead className="w-44">Parent</TableHead>
              <TableHead className="w-24">Storage</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-400">Loading...</TableCell></TableRow>
            ) : locations.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-400">No locations found</TableCell></TableRow>
            ) : locations.map(loc => (
              <TableRow key={loc._id} data-testid={`loc-row-${loc.location_code}`}>
                <TableCell>
                  <div className={`w-7 h-7 rounded flex items-center justify-center text-xs font-bold ${loc.is_virtual ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>
                    {TYPE_ICONS[loc.location_type] || "?"}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{loc.location_code}</TableCell>
                <TableCell className="font-medium">{loc.location_name}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{(loc.location_type || "").replace(/_/g, " ")}</Badge></TableCell>
                <TableCell className="text-xs">{getParentName(loc.parent_location_id)}</TableCell>
                <TableCell className="text-xs">{loc.storage_condition || "—"}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[loc.status] || "bg-zinc-100"}`}>{loc.status}</span>{loc.has_pending_changes && <span className="ml-1 px-1.5 py-0.5 text-[8px] font-semibold bg-amber-100 text-amber-700 rounded-sm">PENDING EDIT</span>}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="icon" variant="ghost" onClick={() => setViewLoc(loc)} data-testid={`view-loc-${loc.location_code}`}><Eye className="w-3.5 h-3.5" /></Button>
                  {admin && <Button size="icon" variant="ghost" onClick={() => { setEditId(loc._id); setFormData({ location_name: loc.location_name || "", location_type: loc.location_type || "", parent_location_id: loc.parent_location_id || "", address: loc.address || "", capacity_units: loc.capacity_units || 0, capacity_uom: loc.capacity_uom || "", storage_condition: loc.storage_condition || "", contact_person_name: loc.contact_person_name || "", contact_phone: loc.contact_phone || "", contact_email: loc.contact_email || "", nearest_landmark: loc.nearest_landmark || "", is_virtual: loc.is_virtual || false }); setShowForm(true); }} data-testid={`edit-loc-${loc.location_code}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                  {loc.status === "draft" && admin && <Button size="sm" variant="outline" onClick={() => handleApprove(loc._id)} data-testid={`approve-loc-${loc.location_code}`}><Check className="w-3 h-3 mr-1" />Approve</Button>}
                  {loc.has_pending_changes && admin && <>
                    <Button size="sm" variant="outline" className="h-6 text-[9px] border-green-300 text-green-700" onClick={async () => { try { await api.put(`/ops/inventory/locations/${loc._id}/pending/approve`); toast.success("Edit approved"); fetchLocations(); } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }}} data-testid={`approve-edit-${loc.location_code}`}>Approve Edit</Button>
                    <Button size="sm" variant="outline" className="h-6 text-[9px] border-red-300 text-red-700" onClick={async () => { const r = prompt("Rejection reason (optional):"); try { await api.put(`/ops/inventory/locations/${loc._id}/pending/reject`, {reason: r||""}); toast.success("Edit rejected"); fetchLocations(); } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }}} data-testid={`reject-edit-${loc.location_code}`}><X className="w-3 h-3 mr-0.5"/>Reject</Button>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-zinc-500" data-testid="locations-count">Showing {locations.length} location(s)</p>

      {/* Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="location-form-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Location" : "New Location (Draft)"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Location Name *</Label>
              <Input value={formData.location_name} onChange={e => setFormData(p => ({ ...p, location_name: e.target.value }))} data-testid="location-name-input" />
            </div>
            <div>
              <Label>Location Type</Label>
              <Select value={formData.location_type} onValueChange={v => setFormData(p => ({ ...p, location_type: v }))}>
                <SelectTrigger data-testid="location-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{locationTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parent Location</Label>
              <Select value={formData.parent_location_id || "_none"} onValueChange={v => setFormData(p => ({ ...p, parent_location_id: v === "_none" ? null : v }))}>
                <SelectTrigger><SelectValue placeholder="None (Top-level)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None (Top-level)</SelectItem>
                  {parentCandidates.map(p => <SelectItem key={p._id} value={p._id}>{p.location_code} — {p.location_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Link to OMS Warehouse</Label>
              <Select value={formData.warehouse_ref_id || "_none"} onValueChange={v => setFormData(p => ({ ...p, warehouse_ref_id: v === "_none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {warehouses.map(w => <SelectItem key={w._id} value={w._id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Storage Condition</Label>
              <Select value={formData.storage_condition} onValueChange={v => setFormData(p => ({ ...p, storage_condition: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{storageConditions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Capacity</Label>
              <Input type="number" value={formData.capacity_units} onChange={e => setFormData(p => ({ ...p, capacity_units: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Capacity UOM</Label>
              <Select value={formData.capacity_uom || "_none"} onValueChange={v => setFormData(p => ({ ...p, capacity_uom: v === "_none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">—</SelectItem>{uomList.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={formData.is_virtual} onCheckedChange={v => setFormData(p => ({ ...p, is_virtual: v }))} />
              <Label>Virtual Location (3P)</Label>
            </div>
            <div className="col-span-2">
              <Label>Address Line 1</Label>
              <Input value={formData.address?.line1 || ""} onChange={e => setFormData(p => ({ ...p, address: { ...p.address, line1: e.target.value } }))} />
            </div>
            <div><Label>City</Label><Input value={formData.address?.city || ""} onChange={e => setFormData(p => ({ ...p, address: { ...p.address, city: e.target.value } }))} /></div>
            <div><Label>Pincode</Label><Input value={formData.address?.pincode || ""} onChange={e => setFormData(p => ({ ...p, address: { ...p.address, pincode: e.target.value } }))} maxLength={6} /></div>
            <div className="col-span-2"><Label>Nearest Landmark</Label><Input value={formData.nearest_landmark || ""} onChange={e => setFormData(p => ({ ...p, nearest_landmark: e.target.value }))} placeholder="e.g. Near NH44, opp. Hyderabad airport" /></div>
            <div className="col-span-2 border-t pt-2"><h4 className="text-xs font-semibold text-zinc-500 mb-1">Contact Details</h4></div>
            <div><Label>Contact Person</Label><Input value={formData.contact_person_name || ""} onChange={e => setFormData(p => ({ ...p, contact_person_name: e.target.value }))} /></div>
            <div><Label>Contact Phone</Label><Input value={formData.contact_phone || ""} onChange={e => setFormData(p => ({ ...p, contact_phone: e.target.value }))} /></div>
            <div className="col-span-2"><Label>Contact Email</Label><Input value={formData.contact_email || ""} onChange={e => setFormData(p => ({ ...p, contact_email: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-location-btn">{saving ? "Saving..." : editId ? "Save Changes" : "Create Draft"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* View Dialog */}
      <Dialog open={!!viewLoc} onOpenChange={() => setViewLoc(null)}><DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Location — {viewLoc?.location_code}</DialogTitle></DialogHeader>
        {viewLoc && <div className="grid grid-cols-2 gap-2 text-sm">{[["Code", viewLoc.location_code], ["Name", viewLoc.location_name], ["Type", (viewLoc.location_type||"").replace(/_/g," ")], ["Parent", getParentName(viewLoc.parent_location_id)], ["Address", viewLoc.address||"—"], ["Storage", viewLoc.storage_condition||"—"], ["Capacity", viewLoc.capacity_units ? `${viewLoc.capacity_units} ${viewLoc.capacity_uom}` : "—"], ["Contact", viewLoc.contact_person_name||"—"], ["Phone", viewLoc.contact_phone||"—"], ["Email", viewLoc.contact_email||"—"], ["Landmark", viewLoc.nearest_landmark||"—"], ["Status", viewLoc.status], ["Created", (viewLoc.created_at||"").slice(0,10)], ["Approved by", viewLoc.approved_by||"—"]].map(([k,v],i) => <div key={i}><span className="text-zinc-500 text-xs">{k}</span><div>{v}</div></div>)}</div>}
      </DialogContent></Dialog>
    </div>
  );
}
